import { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import COLORS from 'styles/colors';
import TYPO, { weight } from 'styles/typography';

import ChevronSvg from 'public/images/icons/chevron.svg';

const ITEM_HEIGHT = 51;

const Container = styled.ul`
  margin: 0 auto;
  position: relative;
  width: 100%;
  max-width: 360px;
  max-height: 50px;
  border-radius: 25px;
  background-color: ${COLORS.white};
  transition: max-height 0.3s ease-out;
  overflow: hidden;

  ${({ isOpen, maxHeight }) =>
    isOpen &&
    css`
      max-height: ${maxHeight}px;
      transition-timing-function: ease-in;
    `}
`;

const Item = styled.li`
  ${TYPO.p2}
  margin: 0 6px;
  padding: 17px 0 15px;
  font-weight: ${weight.semibold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-align: center;
  border-bottom: 1px solid ${COLORS.blueGrey.opacity(0.3)};
  color: ${({ isActive }) =>
    isActive ? COLORS.forest : COLORS.forest.opacity(0.6)};
  cursor: pointer;

  &:last-child {
    padding-bottom: 16px;
    border: 0;
  }
`;

const Chevron = styled(ChevronSvg)`
  position: absolute;
  top: 14px;
  right: 20px;
  width: 17px;
  height: 17px;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transform-origin: 8px 11px;
  transition: 0.4s;

  path {
    stroke-width: 5px;
  }
`;

const Dropdown = ({ items, activeItem = [], onClick, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownItems = items.filter(({ value }) => value !== activeItem);

  let activeLabel = useMemo(() => {
    if (activeItem.includes('/sessions')) {
      return items.find(({ value }) => value === '/my-clays')?.label;
    } else {
      return items.find(({ value }) => value === activeItem)?.label;
    }
  }, [activeItem, items]);

  return (
    <Container
      {...props}
      onClick={() => setIsOpen(!isOpen)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          setIsOpen(!isOpen);
        }
      }}
      isOpen={isOpen}
      maxHeight={items.length * ITEM_HEIGHT}
      tabIndex={0}
    >
      <Item isActive>
        {activeLabel} <Chevron $isOpen={isOpen} />
      </Item>
      {dropdownItems.map(({ label, value }) => (
        <Item
          key={value}
          onClick={() => onClick(value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onClick(value);
            }
          }}
          tabIndex={0}
        >
          {label}
        </Item>
      ))}
    </Container>
  );
};

export default Dropdown;
